import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { bold, int, num } from "../../utils/xlsx";
import {
  cellRight,
  defaultPeriod,
  download,
  EmptyState,
  ExportButton,
  LoadingOverlay,
  money,
  PeriodFilter,
  periodLabel,
  ratio,
  REPORTS,
  sheetHeading,
  StatCard,
  validPeriod,
} from "./common";

// kind: money (₹), times, days, percent, plain ratio. `formula` is the key into the formulas the API returns.
const SECTIONS = [
  {
    title: "Profitability",
    rows: [
      { label: "Net Sales", key: "netSales", kind: "money" },
      { label: "Gross Profit", key: "grossProfit", kind: "money", formula: "grossProfit" },
      { label: "Gross Profit %", key: "grossProfitPercent", kind: "percent", formula: "grossProfitPercent" },
      { label: "Net Profit", key: "netProfit", kind: "money", formula: "netProfit" },
      { label: "Net Profit %", key: "netProfitPercent", kind: "percent", formula: "netProfitPercent" },
      { label: "Return on Investment (ROI) %", key: "returnOnInvestmentPercent", kind: "percent", formula: "returnOnInvestmentPercent" },
      { label: "Return on Working Capital %", key: "returnOnWorkingCapitalPercent", kind: "percent", formula: "returnOnWorkingCapitalPercent" },
    ],
  },
  {
    title: "Liquidity",
    rows: [
      { label: "Current Assets", key: "currentAssets", kind: "money", formula: "currentAssets" },
      { label: "Current Liabilities", key: "currentLiabilities", kind: "money", formula: "currentLiabilities" },
      { label: "Working Capital", key: "workingCapital", kind: "money", formula: "workingCapital" },
      { label: "Current Ratio", key: "currentRatio", kind: "ratio", formula: "currentRatio" },
      { label: "Quick Ratio", key: "quickRatio", kind: "ratio", formula: "quickRatio" },
    ],
  },
  {
    title: "Inventory",
    rows: [
      { label: "Opening Stock", key: "openingStock", kind: "money" },
      { label: "Closing Stock", key: "closingStock", kind: "money" },
      { label: "Average Stock", key: "averageStock", kind: "money" },
      { label: "Cost of Goods Sold", key: "costOfGoodsSold", kind: "money" },
      { label: "Inventory Turnover", key: "inventoryTurnover", kind: "times", formula: "inventoryTurnover" },
      { label: "Inventory Days", key: "inventoryDays", kind: "days", formula: "inventoryDays" },
      { label: "Inventory Turnover (Tally method)", key: "tallyInventoryTurnover", kind: "times", formula: "tallyInventoryTurnover" },
    ],
  },
  {
    title: "Receivables",
    rows: [
      { label: "Credit Sales (incl. GST)", key: "creditSales", kind: "money" },
      { label: "Opening Receivables", key: "openingReceivables", kind: "money" },
      { label: "Closing Receivables", key: "closingReceivables", kind: "money" },
      { label: "Average Receivables", key: "averageReceivables", kind: "money" },
      { label: "Receivable Turnover", key: "receivableTurnover", kind: "times", formula: "receivableTurnover" },
      { label: "Collection Period (days)", key: "receivableDays", kind: "days", formula: "receivableDays" },
      { label: "Recv. Turnover in days (Tally method)", key: "tallyReceivableTurnoverDays", kind: "days", formula: "tallyReceivableTurnoverDays" },
    ],
  },
  {
    title: "Payables",
    rows: [
      { label: "Credit Purchases (incl. GST)", key: "creditPurchases", kind: "money" },
      { label: "Opening Payables", key: "openingPayables", kind: "money" },
      { label: "Closing Payables", key: "closingPayables", kind: "money" },
      { label: "Average Payables", key: "averagePayables", kind: "money" },
      { label: "Payable Turnover", key: "payableTurnover", kind: "times", formula: "payableTurnover" },
      { label: "Payment Period (days)", key: "payableDays", kind: "days" },
    ],
  },
  {
    title: "Capital Structure",
    rows: [
      { label: "Total Debt (Loans incl. OD/CC)", key: "totalDebt", kind: "money" },
      { label: "Capital Account", key: "capitalAccount", kind: "money" },
      { label: "Accumulated Profit & Loss", key: "accumulatedProfit", kind: "money" },
      { label: "Shareholders' Equity", key: "shareholdersEquity", kind: "money" },
      { label: "Debt to Equity Ratio", key: "debtToEquity", kind: "ratio", formula: "debtToEquity" },
      { label: "Total Outside Liabilities ÷ Equity (Tally method)", key: "totalOutsideLiabilitiesToEquity", kind: "ratio", formula: "totalOutsideLiabilitiesToEquity" },
    ],
  },
];

const display = (value, kind) => {
  if (kind === "money") return value === null || value === undefined ? "N/A" : `₹${money(value)}`;
  if (kind === "percent") return ratio(value, "%");
  if (kind === "times") return ratio(value, " times");
  if (kind === "days") return ratio(value, " days");
  return ratio(value);
};

/** Ratio analysis for a period: profitability, liquidity, inventory, receivable and payable turnover, debt-equity, ROI. */
const RatioAnalysis = () => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [applied, setApplied] = useState(null);
  const [section, setSection] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!validPeriod(period)) return;
    setLoading(true);
    try {
      setReport(await api.get(`${REPORTS}/ratios`, { fromDate: period.fromDate, toDate: period.toDate }));
      setApplied(period);
    } catch (error) {
      toast.error(error.message || "Failed to load ratio analysis");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportExcel = () => {
    const rows = [
      ...sheetHeading("Ratio Analysis", periodLabel(applied)),
      ["Days in period", int(report.periodDays)],
      [],
      [bold("Section"), bold("Metric"), bold("Value"), bold("Unit"), bold("Formula")],
    ];
    SECTIONS.forEach((s) =>
      s.rows.forEach((row) =>
        rows.push([
          s.title,
          row.label,
          num(report[row.key]),
          { money: "₹", percent: "%", times: "times", days: "days", ratio: "ratio" }[row.kind],
          (row.formula && report.formulas?.[row.formula]) || "",
        ])
      )
    );
    download(`Ratio Analysis ${periodLabel(applied)}`, [{ name: "Ratio Analysis", rows, widths: [18, 44, 16, 8, 90] }]);
  };

  const visible = SECTIONS.filter((s) => !section || s.title === section);

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Key ratios for the period, with the formula behind each. Turnover ratios use the average of opening and closing balances; "N/A" means the
        denominator is zero for this period.
      </div>
      <div className="filters-bar">
        <PeriodFilter period={period} onChange={setPeriod} />
        <select value={section} onChange={(e) => setSection(e.target.value)} aria-label="Section">
          <option value="">All sections</option>
          {SECTIONS.map((s) => (
            <option key={s.title} value={s.title}>
              {s.title}
            </option>
          ))}
        </select>
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!report} />
      </div>

      {!report ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        <>
          <div className="stat-grid" style={{ marginTop: "16px" }}>
            <StatCard label="Inventory Turnover" value={ratio(report.inventoryTurnover, " times")} hint={`${ratio(report.inventoryDays)} days of stock`} />
            <StatCard label="Receivable Turnover" value={ratio(report.receivableTurnover, " times")} hint={`${ratio(report.receivableDays)} days to collect`} />
            <StatCard label="Debt to Equity" value={ratio(report.debtToEquity)} />
            <StatCard label="Return on Investment" value={ratio(report.returnOnInvestmentPercent, "%")} />
            <StatCard label="Current Ratio" value={ratio(report.currentRatio)} />
          </div>
          {visible.map((s) => (
            <div className="table-wrapper" key={s.title}>
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", width: "32%" }}>{s.title}</th>
                    <th style={{ ...cellRight, width: "18%" }}>Value</th>
                    <th style={{ textAlign: "left" }}>How it is calculated</th>
                  </tr>
                </thead>
                <tbody>
                  {s.rows.map((row) => (
                    <tr key={row.key}>
                      <td style={{ textAlign: "left", fontWeight: row.formula ? 600 : 400 }}>{row.label}</td>
                      <td style={{ ...cellRight, fontWeight: row.formula ? 600 : 400 }}>{display(report[row.key], row.kind)}</td>
                      <td style={{ textAlign: "left", color: "var(--color-text-muted)", fontSize: "12.5px" }}>
                        {(row.formula && report.formulas?.[row.formula]) || ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default RatioAnalysis;
